
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Plus, Loader2, RefreshCw, AlertCircle } from "lucide-react";
import { useToast } from "@/components/ui/use-toast";
import AdminProductsList from "@/components/admin/AdminProductsList";
import AdminProductForm from "@/components/admin/AdminProductForm";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import type { EditingProduct, SizeGuide } from "@/types/product";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";

const AdminProducts = () => {
  const [isCreating, setIsCreating] = useState(false);
  const [editingProductId, setEditingProductId] = useState<string | null>(null);
  const { toast } = useToast();
  const queryClient = useQueryClient();
  
  const { data: editingProduct, isLoading: isLoadingProduct, error: fetchError, refetch } = useQuery({
    queryKey: ['admin-product', editingProductId],
    queryFn: async () => {
      if (!editingProductId) return null;
      console.log("AdminProducts: Fetching product for editing:", editingProductId);

      // Rafraîchir la session pour éviter les erreurs RLS avec token expiré
      const { error: sessionError } = await supabase.auth.getSession();
      if (sessionError) {
        console.error("AdminProducts: Session refresh failed:", sessionError);
      }

      const { data, error } = await supabase
        .from('products')
        .select(`
          id,
          name,
          description,
          price,
          published,
          has_colors,
          has_sizes,
          has_size_guide,
          size_guide,
          product_categories (
            categories (
              name
            )
          ),
          product_variants (
            size,
            color_name,
            color_hex,
            position
          ),
          product_images (
            url,
            main
          )
        `)
        .eq('id', editingProductId)
        .maybeSingle();

      if (error) {
        console.error("AdminProducts: Error fetching product:", error.message, error.details, error.hint);
        throw new Error(`Erreur de chargement: ${error.message}`);
      }

      if (!data) {
        throw new Error("Produit non trouvé");
      }

      console.log("AdminProducts: Product data fetched successfully");

      const sortedVariants = [...data.product_variants].sort((a, b) => 
        (a.position || 0) - (b.position || 0)
      );

      const uniqueColors = data.has_colors ? Array.from(
        new Set(
          sortedVariants
            .filter((v: any) => v.color_name && v.color_hex)
            .map((variant: any) => JSON.stringify({
              name: variant.color_name,
              hex: variant.color_hex
            }))
        )
      ).map(str => JSON.parse(str)) : [];

      const selectedSizes = data.has_sizes ? Array.from(
        new Set(sortedVariants.map((variant: any) => variant.size))
      ).filter(Boolean) as string[] : [];

      return {
        id: data.id,
        name: data.name,
        description: data.description,
        price: data.price,
        categories: data.product_categories.map((pc: any) => pc.categories.name),
        published: data.published,
        size_guide: data.size_guide as SizeGuide | null,
        variants: sortedVariants.map((variant: any) => ({
          size: variant.size,
          colorName: variant.color_name,
          colorHex: variant.color_hex,
          position: variant.position
        })),
        colors: uniqueColors,
        selectedSizes: selectedSizes,
        images: data.product_images.map((image: any) => image.url),
        has_colors: data.has_colors,
        has_sizes: data.has_sizes,
        has_size_guide: data.has_size_guide
      } as EditingProduct;
    },
    enabled: !!editingProductId,
    retry: 1
  });

  const handleSuccess = async () => {
    try {
      await queryClient.invalidateQueries({ queryKey: ['admin-products'] });
      await queryClient.invalidateQueries({ queryKey: ['admin-product', editingProductId] });
      await queryClient.invalidateQueries({ queryKey: ['products'] });

      setIsCreating(false);
      setEditingProductId(null);

      toast({
        title: editingProduct ? "Produit modifié" : "Produit créé",
        description: `Le produit a été ${editingProduct ? "modifié" : "créé"} avec succès`,
      });
    } catch (error) {
      console.error("AdminProducts: Error during cache invalidation:", error);
    }
  };

  const shouldShowForm = isCreating || (editingProductId && !isLoadingProduct && editingProduct);

  // État de chargement visible
  if (editingProductId && isLoadingProduct) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="flex flex-col items-center justify-center py-20 gap-4">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
          <p className="text-muted-foreground">Chargement du produit...</p>
        </div>
      </div>
    );
  }

  // État d'erreur visible
  if (editingProductId && fetchError) {
    return (
      <div className="container mx-auto px-4 py-8">
        <Alert variant="destructive" className="max-w-lg mx-auto">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Impossible de charger le produit</AlertTitle>
          <AlertDescription className="mt-2">
            {(fetchError as Error).message}
          </AlertDescription>
        </Alert>
        <div className="flex justify-center gap-4 mt-6">
          <Button variant="outline" onClick={() => setEditingProductId(null)}>
            Retour à la liste
          </Button>
          <Button onClick={() => refetch()} className="flex items-center gap-2">
            <RefreshCw className="h-4 w-4" />
            Réessayer
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="flex justify-between items-center mb-8">
        <h1 className="text-2xl font-bold">Gestion des produits</h1>
        {!shouldShowForm && (
          <Button 
            onClick={() => setIsCreating(true)}
            className="flex items-center gap-2"
          >
            <Plus className="w-4 h-4" />
            Nouveau produit
          </Button>
        )}
      </div>

      {shouldShowForm ? (
        <AdminProductForm
          productToEdit={editingProduct}
          onSuccess={handleSuccess}
          onCancel={() => {
            setIsCreating(false);
            setEditingProductId(null);
            queryClient.removeQueries({ queryKey: ['admin-product', editingProductId] });
          }}
        />
      ) : (
        <AdminProductsList 
          onEdit={(productId) => {
            console.log("AdminProducts: Edit requested for product:", productId);
            setEditingProductId(productId);
          }}
        />
      )}
    </div>
  );
};

export default AdminProducts;
